/**
 * Permission Service
 * Handles all permission-related API calls
 */

import apiClient from './api';
import type { ApiResponse } from '../types';

export interface Permission {
  id: string;
  tenantId: string;
  role: string;
  resource: string;
  action: string;
  allowed: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface PermissionUpdate {
  role: string;
  resource: string;
  action: string;
  allowed: boolean;
}

/** Response of `GET /permissions/effective` — the caller's own map, plus its caveats. */
export interface EffectivePermissions {
  role: string;
  /** "resource:action" -> allowed. */
  permissions: Record<string, boolean>;
  /**
   * Pairs reported `true` that the backend services further restrict to rows the caller owns.
   * The matrix cannot express ownership, so treating these as unconditional over-promises —
   * show the affordance, but expect the API to refuse on someone else's row.
   */
  ownershipScoped: string[];
  /** Set when a route guard overrides the map wholesale (super_admin); null otherwise. */
  routeGuardOverride: string | null;
}

export const permissionService = {
  /**
   * Get all permissions for the tenant
   */
  getPermissions: async (): Promise<Permission[]> => {
    const response = await apiClient.get<ApiResponse<{ permissions: Permission[] }>>(
      '/permissions'
    );
    if (response.data.success && response.data.data) {
      return response.data.data.permissions;
    }
    throw new Error(response.data.message || 'Failed to fetch permissions');
  },

  /**
   * Update a single permission
   */
  updatePermission: async (update: PermissionUpdate): Promise<Permission> => {
    const response = await apiClient.patch<ApiResponse<{ permission: Permission }>>(
      '/permissions',
      update
    );
    if (response.data.success && response.data.data) {
      return response.data.data.permission;
    }
    throw new Error(response.data.message || 'Failed to update permission');
  },

  /**
   * Bulk update permissions
   */
  bulkUpdatePermissions: async (updates: PermissionUpdate[]): Promise<Permission[]> => {
    const response = await apiClient.put<ApiResponse<{ permissions: Permission[] }>>(
      '/permissions/bulk',
      { updates }
    );
    if (response.data.success && response.data.data) {
      return response.data.data.permissions;
    }
    throw new Error(response.data.message || 'Failed to update permissions');
  },

  /**
   * Reset permissions to defaults
   */
  resetPermissions: async (): Promise<Permission[]> => {
    const response = await apiClient.post<ApiResponse<{ permissions: Permission[] }>>(
      '/permissions/reset'
    );
    if (response.data.success && response.data.data) {
      return response.data.data.permissions;
    }
    throw new Error(response.data.message || 'Failed to reset permissions');
  },

  /**
   * Check if current user has a specific permission
   */
  checkPermission: async (resource: string, action: string): Promise<boolean> => {
    const response = await apiClient.get<ApiResponse<{ hasPermission: boolean }>>(
      '/permissions/check',
      { params: { resource, action } }
    );
    if (response.data.success && response.data.data) {
      return response.data.data.hasPermission;
    }
    return false;
  },

  /**
   * Fetch the current user's whole permission map in ONE request.
   *
   * Replaces the 27 parallel `/permissions/check` calls `usePermissions` used to issue on every
   * page load for a non-admin. Needs no admin rights: it only ever returns the caller's own
   * role, which is why it exists alongside the admin-only `getPermissions` above.
   */
  getEffectivePermissions: async (): Promise<EffectivePermissions> => {
    const response = await apiClient.get<ApiResponse<EffectivePermissions>>(
      '/permissions/effective'
    );
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    // Fail CLOSED. An empty map denies everything, which hides affordances rather than
    // offering ones the API will reject.
    return { role: '', permissions: {}, ownershipScoped: [], routeGuardOverride: null };
  },
};
